import React, { useState, useContext } from "react";
import { API } from "@/config/apiBase";
import { useLocation } from "react-router-dom";
import { MessageSquarePlus, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { AuthContext } from "@/App";

const FeedbackModal = ({ open, onOpenChange }) => {
  const location = useLocation();
  const { user } = useContext(AuthContext) || {};
  
  const [feedbackType, setFeedbackType] = useState("general");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Detect mobile
  const isMobile = window.innerWidth < 768;
  
  const feedbackTypes = [
    { value: "general", label: "General feedback" },
    { value: "bug", label: "Bug or something broken" },
    { value: "confusing", label: "Something confusing" },
    { value: "feature_request", label: "Feature request" }
  ];
  
  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error("Please enter your feedback");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/feedback`, {
        feedback_type: feedbackType,
        message: message.trim(),
        email: email.trim() || null,
        current_route: location.pathname,
        is_mobile: isMobile
      });
      
      if (response.data.success) {
        toast.success("Thank you! We really appreciate the feedback 💛");
        // Reset form
        setMessage("");
        setFeedbackType("general");
        onOpenChange(false);
      }
    } catch (error) {
      console.error("Feedback submission failed:", error);
      toast.error("Failed to send feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-white rounded-3xl border-[#F4F1EA]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl" style={{ fontFamily: 'Fraunces, serif' }}>
            <MessageSquarePlus className="w-5 h-5 text-[#E07A5F]" />
            Help us improve OuttaTouch
          </DialogTitle>
          <DialogDescription className="text-[#6D6F7C]">
            We read every message. Tell us what&apos;s working or what&apos;s missing.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Feedback Type */}
          <div>
            <Label className="text-sm text-[#3D405B]">What kind of feedback?</Label>
            <Select value={feedbackType} onValueChange={setFeedbackType}>
              <SelectTrigger className="mt-1 bg-white border-[#F4F1EA] rounded-xl" data-testid="feedback-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#F4F1EA] rounded-xl">
                {feedbackTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Message */}
          <div>
            <Label className="text-sm text-[#3D405B]">Your feedback</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What were you trying to do? What happened instead?"
              className="mt-1 bg-white border-[#F4F1EA] rounded-xl min-h-[120px] resize-none"
              data-testid="feedback-message"
            />
          </div>
          
          {/* Email (optional) */}
          <div>
            <Label className="text-sm text-[#3D405B]">
              Email <span className="text-[#9CA3AF]">(optional, for follow-up)</span>
            </Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="mt-1 bg-white border-[#F4F1EA] rounded-xl"
              data-testid="feedback-email"
            />
          </div>
          
          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !message.trim()}
            className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full disabled:opacity-50"
            data-testid="feedback-submit"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                Sending...
              </span>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send feedback
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModal;
